<template>
  <div class="app-mesh">
    <div class="app-mesh-bg" aria-hidden="true" />
    <template v-if="layout === 'default'">
      <MobileOverlay :open="mobileOpen" @close="mobileOpen = false" />
      <AppSidebar
        :mobile-open="mobileOpen"
        :is-admin="isAdminRoute"
        :is-authenticated="isAuthenticated"
        :active-section="activeSection"
        @close-mobile="mobileOpen = false"
        @sign-out="handleSignOut"
      />
      <div class="lg:pl-72 flex flex-col min-h-screen min-w-0 w-full overflow-x-hidden">
        <AppTopbar
          :page-title="pageTitle"
          :page-description="pageDescription"
          @toggle-mobile="mobileOpen = true"
        />
        <main class="flex-1 min-w-0 w-full overflow-x-hidden">
          <router-view />
        </main>
      </div>
    </template>

    <template v-else-if="layout === 'auth'">
      <div class="min-h-screen flex items-center justify-center p-4 app-mesh relative overflow-hidden">
        <div class="app-mesh-bg" aria-hidden="true" />
        <img
          :src="images.signInBackground"
          alt=""
          class="absolute inset-0 w-full h-full object-cover opacity-15"
          aria-hidden="true"
        />
        <div class="relative z-10 w-full flex justify-center">
          <router-view />
        </div>
      </div>
    </template>

    <ToastContainer />
  </div>
</template>

<script>
import AppSidebar from '@/components/layout/AppSidebar.vue'
import AppTopbar from '@/components/layout/AppTopbar.vue'
import MobileOverlay from '@/components/layout/MobileOverlay.vue'
import ToastContainer from '@/components/ui/ToastContainer.vue'
import { images } from '@/data/images'
import { getActiveSection } from '@/utils/scrollSpy'
import { useAuth } from '@/composables/useAuth'
import { trackPageView } from '@/services/pageAnalytics'

export default {
  name: 'App',
  components: { AppSidebar, AppTopbar, MobileOverlay, ToastContainer },
  setup() {
    const { isAuthenticated, signOut } = useAuth()
    return { isAuthenticated, signOut }
  },
  data() {
    return { mobileOpen: false, images, activeSection: 'home', onScrollSpy: null, scrollSpyFrame: null }
  },
  computed: {
    layout() {
      return this.$route.meta?.layout || 'default'
    },
    isAdminRoute() {
      return this.$route.path.startsWith('/admin')
    },
    pageTitle() {
      return this.pageMeta.title
    },
    pageDescription() {
      return this.pageMeta.description
    },
    pageMeta() {
      const sections = {
        home: {
          title: 'Home',
          description: 'Full Stack Developer · 3+ years experience',
        },
        about: {
          title: 'About',
          description: 'Background, skills, and what I do',
        },
        resume: {
          title: 'Resume',
          description: 'Experience, education, and qualifications',
        },
        contact: {
          title: 'Contact',
          description: 'Get in touch for opportunities or collaborations',
        },
      }

      if (this.$route.path === '/') {
        return sections[this.activeSection] || sections.home
      }

      const pages = {
        '/admin': { title: 'Admin Dashboard', description: 'Manage your portfolio content' },
        '/admin/profile': { title: 'Edit Profile', description: 'Hero, about, and social links' },
        '/admin/resume': { title: 'Edit Resume', description: 'Experience, education, and skills' },
        '/admin/messages': { title: 'Messages', description: 'Contact form submissions' },
        '/admin/analytics': { title: 'Analytics', description: 'Page visits and traffic overview' },
        '/admin/portfolio': { title: 'Admin Portfolio', description: 'Manage portfolio entries' },
        '/admin/portfolio/create': { title: 'Add Project', description: 'Create a new portfolio item' },
      }
      return pages[this.$route.path] || { title: 'Portfolio', description: '' }
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(route) {
        this.mobileOpen = false
        if (route.path === '/') {
          this.activeSection = route.hash?.replace('#', '') || 'home'
          this.$nextTick(() => this.initScrollSpy())
        } else {
          this.teardownScrollSpy()
        }
      },
    },
  },
  mounted() {},
  beforeUnmount() {
    this.teardownScrollSpy()
  },
  methods: {
    initScrollSpy() {
      this.teardownScrollSpy()
      if (this.$route.path !== '/') return

      const updateSection = () => {
        const next = getActiveSection()
        if (next !== this.activeSection) {
          this.activeSection = next
          trackPageView({ path: '/', hash: `#${next}`, fullPath: `/#${next}` })
        }
      }

      this.onScrollSpy = () => {
        if (this.scrollSpyFrame) cancelAnimationFrame(this.scrollSpyFrame)
        this.scrollSpyFrame = requestAnimationFrame(updateSection)
      }

      window.addEventListener('scroll', this.onScrollSpy, { passive: true })
      window.addEventListener('resize', this.onScrollSpy, { passive: true })
      this.$nextTick(updateSection)
    },
    teardownScrollSpy() {
      if (this.onScrollSpy) {
        window.removeEventListener('scroll', this.onScrollSpy)
        window.removeEventListener('resize', this.onScrollSpy)
        this.onScrollSpy = null
      }
      if (this.scrollSpyFrame) {
        cancelAnimationFrame(this.scrollSpyFrame)
        this.scrollSpyFrame = null
      }
    },
    async handleSignOut() {
      await this.signOut()
      if (this.$route.path.startsWith('/admin')) {
        this.$router.push('/signin')
      }
    },
  },
}
</script>

<style>
.page-enter-active,
.page-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.page-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.page-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
